import { useState } from 'react';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { usePermissions } from '@/shared/permissions';
import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import type { Employee, EmployeeRole } from './employeesApi';
import {
  useChangeEmployeeRole,
  useDeactivateEmployee,
  useEmployees,
  useInviteEmployee,
} from './hooks';

const ASSIGNABLE_ROLES: EmployeeRole[] = ['ADMIN', 'MANAGER', 'STAFF'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function statusColor(status: Employee['status']): 'success' | 'warning' | 'default' {
  if (status === 'ACTIVE') return 'success';
  if (status === 'INVITED') return 'warning';
  return 'default';
}

/** Team / employee management for the active tenant (gated by `employees.manage`). */
export function EmployeesPage() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('employees.manage');

  const query = useEmployees({ enabled: canManage });
  const invite = useInviteEmployee();
  const changeRole = useChangeEmployeeRole();
  const deactivate = useDeactivateEmployee();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<EmployeeRole>('STAFF');
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  if (!canManage) {
    return (
      <PageContainer title="Team">
        <ErrorState
          title="Access restricted"
          message="You don't have permission to manage employees."
        />
      </PageContainer>
    );
  }

  const employees = query.data ?? [];
  const inviteValid = EMAIL_RE.test(inviteEmail.trim());

  const submitInvite = () => {
    invite.mutate(
      { email: inviteEmail.trim(), role: inviteRole },
      {
        onSuccess: () => {
          setInviteOpen(false);
          setInviteEmail('');
          setInviteRole('STAFF');
          setToast('Invitation sent.');
        },
        onError: (error) => setToast(error.message),
      },
    );
  };

  return (
    <PageContainer
      title="Team"
      subtitle="Invite employees, assign roles, and manage access for this company."
      actions={
        <Button
          variant="contained"
          startIcon={<PersonAddAlt1RoundedIcon />}
          onClick={() => setInviteOpen(true)}
        >
          Invite employee
        </Button>
      }
    >
      {query.isLoading ? (
        <LoadingState label="Loading team…" />
      ) : query.isError ? (
        <ErrorState
          title="Couldn't load the team"
          message={query.error?.message ?? 'An unexpected error occurred.'}
          onRetry={() => void query.refetch()}
        />
      ) : employees.length === 0 ? (
        <EmptyState
          title="No employees yet"
          description="Invite your first teammate to collaborate."
          action={
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1RoundedIcon />}
              onClick={() => setInviteOpen(true)}
            >
              Invite employee
            </Button>
          }
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee) => {
                const isOwner = employee.role === 'OWNER';
                return (
                  <TableRow key={employee.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {employee.name ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isOwner ? (
                        <Chip size="small" color="primary" label="Owner" />
                      ) : (
                        <TextField
                          select
                          size="small"
                          value={employee.role}
                          disabled={changeRole.isPending}
                          onChange={(e) =>
                            changeRole.mutate(
                              { id: employee.id, role: e.target.value as EmployeeRole },
                              { onError: (err) => setToast(err.message) },
                            )
                          }
                          sx={{ minWidth: 140 }}
                        >
                          {ASSIGNABLE_ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role.charAt(0) + role.slice(1).toLowerCase()}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={statusColor(employee.status)}
                        label={employee.status.charAt(0) + employee.status.slice(1).toLowerCase()}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={isOwner ? 'The owner cannot be removed' : 'Deactivate'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={isOwner}
                            aria-label={`Deactivate ${employee.email}`}
                            onClick={() => setDeleteTarget(employee)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Invite employee</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Work email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              error={inviteEmail.length > 0 && !inviteValid}
              fullWidth
              autoFocus
              helperText="They'll receive an invitation to set a password."
            />
            <TextField
              select
              label="Role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as EmployeeRole)}
              fullWidth
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {role.charAt(0) + role.slice(1).toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitInvite}
            disabled={invite.isPending || !inviteValid}
            startIcon={invite.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Send invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirm */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Deactivate employee?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteTarget?.email}</strong> will lose access to this company. You can
            re-invite them later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deactivate.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deactivate.isPending}
            onClick={() => {
              if (!deleteTarget) return;
              const target = deleteTarget;
              deactivate.mutate(target.id, {
                onSuccess: () => {
                  setDeleteTarget(null);
                  setToast(`${target.email} deactivated.`);
                },
                onError: (error) => setToast(error.message),
              });
            }}
            startIcon={
              deactivate.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast(null)} severity="info" variant="filled" sx={{ width: '100%' }}>
          {toast ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
